// A "transport" error is a failure to complete the HTTP round-trip (the
// response never reached the browser) rather than an application-level error
// the server returned. It matters for non-idempotent mutations like network
// duplication: the server transaction can still have committed, so the client
// must reconcile instead of reporting an outright failure (KBE-738).
//
// Browsers phrase the same failure differently: Chrome "Failed to fetch",
// Safari "Load failed", Firefox "NetworkError when attempting to fetch
// resource". tRPC wraps these in a TRPCClientError whose `cause` is the
// original TypeError, so we walk the cause chain.
const TRANSPORT_ERROR_PATTERN =
  /failed to fetch|load failed|network\s?error|network request failed|fetch failed|err_network|connection (?:closed|reset|refused)|the network connection was lost/i

export function isTransportError(error: unknown): boolean {
  const messages: string[] = []
  let current: unknown = error
  const seen = new Set<unknown>()
  while (current != null && !seen.has(current)) {
    seen.add(current)
    if (current instanceof Error) {
      messages.push(current.message)
      current = current.cause
    } else if (typeof current === 'string') {
      messages.push(current)
      break
    } else {
      break
    }
  }
  // Test each message on its own — joining them could let a phrase match
  // across a boundary (e.g. an app error ending "…network" + a cause starting
  // "error…") and misclassify an application error as a transport failure.
  return messages.some((message) => TRANSPORT_ERROR_PATTERN.test(message))
}
